import { v4 as uuidv4 } from 'uuid';

import appSettings from 'wanderu-config';
import Logger from 'wanderu-logger';

import { experimentEligibilityReceived } from './experimentsSlice';
import { variantsFetchFailed, variantsReceived } from './variantsSlice';

import type { ExperimentClient } from '@amplitude/experiment-js-client';
import type { RemoteEvaluationClient } from '@amplitude/experiment-node-server';
import type { Request, Response } from 'express';
import type { Cookies } from 'react-cookie';
import type { Dispatch } from 'redux';

const logger = Logger.getLogger('wanderu-analytics');
const CF_BOT_SCORE = 5;
const BLOCKED_ASN_LIST = [15169, 199661, 36116, 8075, 714, 13238, 13414];

// Sensitive user properties that should only be sent server-side
interface SensitiveUserProperties {
    asn?: string;
    cfBotScore?: number;
    ipAddress?: string;
    ja4?: string;
}

// Client-safe user properties that can be returned in the API response
interface ClientUserProperties {
    accept?: string;
    acceptLanguage?: string;
    userAgent?: string;
}

// Combined properties for internal use
interface AllUserProperties extends ClientUserProperties, SensitiveUserProperties {}

// Matches Cloudflare list: https://dash.cloudflare.com/30203568562fe5fb374e30d278f53e3d/wanderu.com/caching/cache-rules/abdc0352ab324c39a579f27e12ddda25
const NON_CACHED_URLS = [
    '/account/',
    '/checkout/',
    '/depart/',
    '/explore/',
    '/return/',
    '/travel-review/',
    '/tripsummary/',
];

const isNonCachedUrl = (url: string) => {
    const urlToTest = url.split('?')[0];

    // homepage is not cached
    if (urlToTest === '/' || /^\/[a-z]{2}-[a-z]{2}\/$/.test(urlToTest)) return true;

    // explore landing page with no url segments after it is cached
    if (urlToTest.endsWith('/explore/')) return false;

    return NON_CACHED_URLS.some((segment) => urlToTest.includes(segment));
};

let experiment: RemoteEvaluationClient;
let amplitudeNodeInitialized = false;

type IneligibilityReason = string | null;
type EligibilityReturnValue = [boolean, IneligibilityReason, AllUserProperties];

function getIsEligibleForAssignment(req: Request): EligibilityReturnValue {
    const asn = String(req?.headers?.['x-visitor-asn']);
    const cfBotScore = Number(req?.headers?.['cf-bot-score']);
    const ipAddress = String(req?.headers?.['x-real-ip']);
    const ja4 = String(req?.headers?.['cf-ja4']);
    const userAgent = req?.headers?.['user-agent'] || '';
    const acceptLanguage = req?.headers?.['accept-language'];
    const accept = req?.headers?.accept;

    const userProperties = {
        accept,
        acceptLanguage,
        asn,
        cfBotScore,
        ipAddress,
        ja4,
        userAgent,
    };

    // Check Cloudflare bot score - 0 is falsy which is fine because that means "not computed" by cloudflare
    const shouldBlockBotScore = cfBotScore && cfBotScore < Number(CF_BOT_SCORE);
    const shouldBlockASN = asn && BLOCKED_ASN_LIST.includes(Number(asn));
    const shouldBlockUserAgent = userAgent?.includes('Google-Travel');

    if (shouldBlockBotScore || shouldBlockASN || shouldBlockUserAgent) {
        let label = 'Cloudflare bot score';
        if (shouldBlockASN) label = 'ASN';
        if (shouldBlockUserAgent) label = 'Google Travel user agent';

        return [false, label, userProperties];
    }

    // Check for missing/suspicious headers
    if (!userAgent || !acceptLanguage) {
        return [false, 'missing/suspicious headers', userProperties];
    }

    // Missing critical browser headers
    if (!accept || !accept.includes('text/html')) {
        return [false, 'missing critical browser headers', userProperties];
    }

    return [true, null, userProperties];
}

interface FetchServerVariantsParams {
    cookies: Cookies;
    dispatch: Dispatch;
    req: Request;
    res: Response;
}

const amplitudeLoggerProvider = {
    debug: (message: string, ...additionalInfo: []) => {
        logger.debug('amplitudeLoggerProvider', message, additionalInfo);
    },

    error: (message: string, ...additionalInfo: []) => {
        logger.error('amplitudeLoggerProvider', message, additionalInfo);
    },

    info: (message: string, ...additionalInfo: []) => {
        logger.info('amplitudeLoggerProvider', message, additionalInfo);
    },

    warn: (message: string, ...additionalInfo: []) => {
        logger.warn('amplitudeLoggerProvider', message, additionalInfo);
    },
};

interface ServerSideIdentifyParams {
    deviceId: string;
    ineligibilityReason: IneligibilityReason;
    isEligibleForAssignment: boolean;
    sensitiveProperties: SensitiveUserProperties;
}

async function sendServerSideAmplitudeIdentify({
    deviceId,
    ineligibilityReason,
    isEligibleForAssignment,
    sensitiveProperties,
}: ServerSideIdentifyParams) {
    const apiKey = appSettings?.config?.amplitude?.apiKey;

    if (!apiKey || typeof window !== 'undefined') {
        return;
    }

    try {
        // eslint-disable-next-line import/no-extraneous-dependencies, global-require, @typescript-eslint/no-require-imports
        const { Identify, identify, init } = require('@amplitude/analytics-node');

        if (!amplitudeNodeInitialized) {
            init(apiKey, {
                flushIntervalMillis: 0,
                flushQueueSize: 1,
            });
            amplitudeNodeInitialized = true;
        }

        const identifyEvent = new Identify();

        identifyEvent.set('is_eligible_for_assignment', isEligibleForAssignment);

        if (ineligibilityReason) {
            identifyEvent.set('ineligibility_reason', ineligibilityReason);
        }

        const {
            asn,
            cfBotScore,
            ipAddress,
            ja4,
        } = sensitiveProperties;

        if (cfBotScore) identifyEvent.set('cloudflare_bot_score', cfBotScore);
        if (asn) identifyEvent.set('asn', asn);
        if (ipAddress) identifyEvent.set('ip_address', ipAddress);
        if (ja4) identifyEvent.set('ja4', ja4);

        await identify(identifyEvent, { device_id: deviceId });

        logger.debug('serverSideAmplitudeIdentify', 'sent', { deviceId });
    } catch (error) {
        logger.error('serverSideAmplitudeIdentify', 'failed', { deviceId, error });
    }
}

async function fetchServerVariants({
    cookies,
    dispatch,
    req,
    res,
}: FetchServerVariantsParams) {
    const isNonCachedPage = isNonCachedUrl(req.url);
    const apiKey = appSettings?.config?.amplitude?.apiKey;
    const isServerSide = typeof window === 'undefined';

    if (!apiKey || !isServerSide || !isNonCachedPage) {
        return;
    }

    const deviceId = await handleAmplitudeCookies(cookies, res);

    const [
        isEligibleForAssignment,
        ineligibilityReason,
        allUserProperties,
    ] = getIsEligibleForAssignment(req);

    // Extract sensitive properties for server-side tracking only
    const {
        asn,
        cfBotScore,
        ipAddress,
        ja4,
        ...clientSafeProperties
    } = allUserProperties;

    // Log with full details server-side (this stays in server logs, not sent to client)
    logger.event('isEligibleForAssignment', {
        deviceId,
        ineligibilityReason,
        isEligibleForAssignment,
        ...allUserProperties,
    });

    // Send sensitive data to Amplitude server-side
    sendServerSideAmplitudeIdentify({
        deviceId,
        ineligibilityReason,
        isEligibleForAssignment,
        sensitiveProperties: isEligibleForAssignment
            ? {
                asn,
                cfBotScore,
                ipAddress,
                ja4,
            }
            : {},
    });

    // Dispatch only client-safe data to Redux (this gets serialized to client)
    dispatch(experimentEligibilityReceived({
        deviceId,
        isEligibleForAssignment,
        userProperties: clientSafeProperties,
    }));

    if (!isEligibleForAssignment) {
        return;
    }

    if (!experiment) {
        // eslint-disable-next-line import/no-extraneous-dependencies, global-require, @typescript-eslint/no-require-imports
        const { Experiment: AmplitueExperiment, LogLevel } = require('@amplitude/experiment-node-server');

        const config = {
            fetchRetries: 1,
            fetchRetryBackoffMinMillis: 0,
            fetchRetryTimeoutMillis: 500,
            fetchTimeoutMillis: 500,
            loggerProvider: amplitudeLoggerProvider,
            logLevel: LogLevel.Error,
        };

        // eslint-disable-next-line import/no-extraneous-dependencies, global-require, @typescript-eslint/no-require-imports
        experiment = AmplitueExperiment
            .initializeRemote(apiKey, config);
    }

    try {
        const variants = await experiment
            .fetchV2({ device_id: deviceId });

        dispatch(variantsReceived({ variants }));
    } catch (error) {
        // Don't dispatch variantsFetchFailed() here — it sets hasFetched: true,
        // which prevents the client from retrying. By leaving hasFetched as false,
        // the client's AmplitudeProvider will fall back to fetching variants itself.
        logger.error('Amplitude', 'server_fetch_failed', {
            deviceId,
            error,
        });
    }
}

interface FetchClientVariantsParams {
    experiment: ExperimentClient;
    deviceId: string | undefined;
    dispatch: Dispatch;
}

async function fetchClientVariants({
    deviceId,
    dispatch,
    experiment: clientExperiment,
}: FetchClientVariantsParams) {
    try {
        await clientExperiment.fetch({ device_id: deviceId });
    } catch (error) {
        logger.error('Amplitude', 'fetch_failed', {
            deviceId,
            error,
        });
        dispatch(variantsFetchFailed());
        return;
    }

    const variants = clientExperiment.all();

    dispatch(variantsReceived({ variants }));
}

interface FetchExperimentEligibilityFromRequestParams {
    cookies: Cookies;
    req: Request;
    res: Response;
}

async function fetchExperimentEligibilityFromRequest({
    cookies,
    req,
    res,
}: FetchExperimentEligibilityFromRequestParams) {
    const deviceId = await handleAmplitudeCookies(cookies, res);
    const [
        isEligibleForAssignment,
        ineligibilityReason,
        allUserProperties,
    ] = getIsEligibleForAssignment(req);

    // Extract sensitive properties for server-side tracking only
    const {
        asn,
        cfBotScore,
        ipAddress,
        ja4,
        ...clientSafeProperties
    } = allUserProperties;

    // Send eligibility data to Amplitude server-side (non-blocking)
    // Only include sensitive properties for eligible users to avoid polluting data with bot traffic
    sendServerSideAmplitudeIdentify({
        deviceId,
        ineligibilityReason,
        isEligibleForAssignment,
        sensitiveProperties: isEligibleForAssignment
            ? {
                asn,
                cfBotScore,
                ipAddress,
                ja4,
            }
            : {},
    });

    // Return only client-safe data with obfuscated ineligibility reason
    // This prevents attackers from learning why they were blocked
    return {
        deviceId,
        isEligibleForAssignment,
        userProperties: clientSafeProperties,
    };
}

async function fetchExperimentEligibility() {
    const response = await fetch('/api/experiment-eligibility', {
        credentials: 'include',
        headers: { Accept: 'text/html' },
    });

    if (!response.ok) {
        throw new Error(`Eligibility request failed: ${response.status}`);
    }

    return response.json();
}

async function handleAmplitudeCookies(cookies: Cookies, res: Response) { // https://amplitude.com/docs/sdks/experiment-sdks/experiment-node-js#access-amplitude-cookies
    let deviceId: string | undefined;

    const apiKey = appSettings?.config?.amplitude?.apiKey;

    // eslint-disable-next-line import/no-extraneous-dependencies, global-require, @typescript-eslint/no-require-imports
    const { AmplitudeCookie } = require('@amplitude/experiment-node-server');

    // Get the cookie name for the Amplitude API key
    const ampCookieName = AmplitudeCookie.cookieName(apiKey, true);

    const cookie = cookies.get(ampCookieName);
    // Try to get device ID from existing cookie
    if (cookie) {
        deviceId = AmplitudeCookie.parse(cookie, true).device_id;
    }

    // If no device ID found, generate a new one and set the cookie
    if (!deviceId) {
        deviceId = uuidv4();

        const ampCookieValue = AmplitudeCookie.generate(deviceId, true);

        cookies.set(ampCookieName, ampCookieValue);
        res.cookie(ampCookieName, ampCookieValue, {
            domain: '.wanderu.com', // this should be the same domain used by the Amplitude JS SDK
            httpOnly: false,
            secure: false,
        });
    }

    return deviceId;
}

export {
    fetchClientVariants,
    fetchExperimentEligibility,
    fetchExperimentEligibilityFromRequest,
    fetchServerVariants,
    getIsEligibleForAssignment,
    isNonCachedUrl,
};
